import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
  DollarSign, Lock, Unlock, AlertTriangle,
  CheckCircle, CreditCard, Banknote, Building, X,
  TrendingUp, Eye, RefreshCw, ShoppingBag,
  ChevronDown, ChevronUp, Gem, Clock, Users, Printer, Store
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api, { API_URL } from '../../services/api';
import RapportJour from './RapportJour';
import { notifySuccess, notifyError } from '../../utils/notifications';

const ClotureCaisse = () => {
  const [activeTab, setActiveTab] = useState('classique'); // 'classique' | 'rapport-jour'
  const [rapport, setRapport] = useState(null);
  const [caisseActive, setCaisseActive] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modals
  const [showOuvrirModal, setShowOuvrirModal] = useState(false);
  const [showCloturerModal, setShowCloturerModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [showVentes, setShowVentes] = useState(false);
  const [showComptes, setShowComptes] = useState(false);
  const [detailVente, setDetailVente] = useState(null);
  const [loadingDetailVente, setLoadingDetailVente] = useState(false);

  const loadDetailVente = async (venteId) => {
    try {
      setLoadingDetailVente(true);
      const r = await api.get(`${API_URL}/ventes/${venteId}/details`);
      setDetailVente(r.data);
    } catch (e) {
      setError('Erreur chargement detail vente');
    } finally {
      setLoadingDetailVente(false);
    }
  };

  // Comptes clients
  const [comptesClients, setComptesClients] = useState([]);
  const [filtreCompte, setFiltreCompte] = useState('');

  // Ouvrir caisse
  const [fondDeCaisse, setFondDeCaisse] = useState(0);

  // Cloturer caisse
  const [billets200, setBillets200] = useState(0);
  const [billets100, setBillets100] = useState(0);
  const [billets50, setBillets50] = useState(0);
  const [billets20, setBillets20] = useState(0);
  const [pieces10, setPieces10] = useState(0);
  const [pieces5, setPieces5] = useState(0);
  const [pieces2, setPieces2] = useState(0);
  const [pieces1, setPieces1] = useState(0);
  const [pieces05, setPieces05] = useState(0);
  const [carteComptee, setCarteComptee] = useState(0);
  const [chequeCompte, setChequeCompte] = useState(0);
  const [virementCompte, setVirementCompte] = useState(0);
  const [commentaire, setCommentaire] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Helpers
  const getBoutiqueId = () => {
    try {
      const ctx = JSON.parse(localStorage.getItem('workspaceContext') || '{}');
      return ctx.boutique_id || ctx.boutiqueId || 1;
    } catch { return 1; }
  };
  const getBoutiqueNom = () => {
    try {
      const ctx = JSON.parse(localStorage.getItem('workspaceContext') || '{}');
      return ctx.boutique_nom || ctx.boutiqueNom || 'Boutique';
    } catch { return 'Boutique'; }
  };

  const boutiqueId = getBoutiqueId();
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const isCentral = (() => { try { return JSON.parse(localStorage.getItem('workspaceContext') || '{}').type === 'central'; } catch { return false; } })();

  const calculerEspeces = () =>
    billets200*200 + billets100*100 + billets50*50 + billets20*20 +
    pieces10*10 + pieces5*5 + pieces2*2 + pieces1*1 + pieces05*0.5;

  const calculerTotalCompte = () =>
    calculerEspeces() + parseFloat(carteComptee||0) + parseFloat(chequeCompte||0) + parseFloat(virementCompte||0);

  const fmt = (m) => parseFloat(m||0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MAD';
  const fmtPdf = (m) => { const n = parseFloat(m||0); const parts = n.toFixed(2).split('.'); parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' '); return parts.join(',') + ' MAD'; };

  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };
  const fmtHeure = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('fr-FR', { hour:'2-digit', minute:'2-digit' });
  };

  const ecartColor = (e) => {
    const a = Math.abs(e||0);
    if (a === 0) return 'text-green-600 bg-green-50 border-green-200';
    if (a < 100) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  // API
  const loadRapport = async () => {
    try {
      const params = isCentral ? 'mode=central' : `boutique_id=${boutiqueId}`;
      const r = await api.get(`${API_URL}/clotures-caisse/rapport-journalier?${params}`);
      setRapport(r.data);
      setCaisseActive(r.data.caisse_ouverte);
    } catch (err) { console.error(err); }
  };

  const loadHistorique = async () => {
    try {
      const r = await api.get(`${API_URL}/clotures-caisse?boutique_id=${boutiqueId}`);
      setHistorique(Array.isArray(r.data) ? r.data : []);
    } catch (err) { console.error(err); }
  };

  const loadComptes = async () => {
    try {
      const r = await api.get(`${API_URL}/clotures-caisse/comptes-clients?boutique_id=${boutiqueId}`);
      setComptesClients(r.data || []);
    } catch (err) { console.error(err); }
  };

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadRapport(), loadHistorique(), loadComptes()]);
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // --- Impression PDF caisse journaliere ---
  // Si boutiqueIdParam/dateParam fournis (depuis RapportJour), fetch frais
  // Sinon utilise le state parent (mode Vue classique)
  const imprimerCaisse = async (boutiqueIdParam = null, dateParam = null) => {
    let rapportData = rapport;
    let caisseActiveData = caisseActive;
    let boutiqueNomData = getBoutiqueNom();
    let dateImpression = today;

    if (boutiqueIdParam && dateParam) {
      try {
        const r = await api.get(`${API_URL}/clotures-caisse/rapport-journalier?boutique_id=${boutiqueIdParam}&date=${dateParam}`);
        rapportData = r.data;
        caisseActiveData = r.data.caisse_ouverte;
        dateImpression = dateParam;
        // Chercher le nom de la boutique dans les donnees si dispo
        if (r.data.ventes && r.data.ventes[0]?.boutique_nom) {
          boutiqueNomData = r.data.ventes[0].boutique_nom;
        }
      } catch (err) {
        console.error('Erreur fetch rapport pour impression:', err);
        setError('Erreur chargement donnees impression');
        return;
      }
    }
    if (!rapportData) return;
    const rapport_local = rapportData;
    const caisseActive_local = caisseActiveData;
    const boutiqueNom_local = boutiqueNomData;

    const doc = new jsPDF('l', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' });
    const heureStr = now.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });

    // Header
    doc.setFillColor(88, 28, 135);
    doc.rect(0, 0, pageW, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT DE CAISSE JOURNALIER', pageW / 2, 12, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${boutiqueNom_local} - ${dateStr} a ${heureStr}`, pageW / 2, 22, { align: 'center' });

    let y = 38;
    doc.setTextColor(0, 0, 0);

    // Indiquer la date des donnees si differente d'aujourd'hui
    if (dateImpression && dateImpression !== today) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(180, 50, 50);
      doc.text(`Donnees du jour : ${dateImpression}`, 14, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      y += 8;
    }

    if (caisseActive_local) {
      doc.setFontSize(9);
      doc.text(`Caisse ouverte depuis: ${fmtDate(caisseActive_local.date_ouverture)}   -   Fond: ${fmtPdf(caisseActive_local.fond_de_caisse)}`, 14, y);
      y += 8;
    }

    const r = rapport_local.resume || {};
    const e = rapport_local.encaissements || {};
    doc.setFontSize(9);
    doc.text(`Ventes: ${r.nb_ventes || 0}   |   Total: ${fmtPdf(r.total_prix_vente)}   |   Remises: ${fmtPdf(r.total_remises)}   |   Encaisse: ${fmtPdf(r.total_encaisse)}`, 14, y);
    y += 8;

    // Encaissements
    autoTable(doc, {
      startY: y,
      head: [['Mode de paiement', 'Montant']],
      body: [
        ['Especes', fmtPdf(e.especes)],
        ['Carte bancaire', fmtPdf(e.carte)],
        ['Cheque', fmtPdf(e.cheque)],
        ['Virement', fmtPdf(e.virement)],
        ['Or (deduction)', fmtPdf(e.or)],
        ['Compte client', fmtPdf(e.compte)],
      ],
      foot: [['TOTAL', fmtPdf(e.total)]],
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [88, 28, 135], textColor: 255 },
      footStyles: { fillColor: [88, 28, 135], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Mouvements detailles chronologiques
    const mouvements = rapport_local.mouvements || [];
    const labelType = (t) => ({ vente: 'Vente', depot_client: 'Depot', encaissement_credit: 'Encaissement' }[t] || t);
    const labelMode = (m) => ({ especes: 'especes', carte: 'carte', cheque: 'cheque', virement: 'virement', autre: 'autre' }[m] || m || '-');
    if (mouvements.length > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`MOUVEMENTS DU JOUR (${mouvements.length})`, 14, y);
      let cumul = 0;
      const rows = mouvements.map(mvt => {
        const montant = parseFloat(mvt.montant) || 0;
        cumul += montant;
        return [
          mvt.heure || '-',
          labelType(mvt.type),
          mvt.client_nom || '-',
          mvt.detail || '-',
          mvt.reference || '-',
          labelMode(mvt.mode_paiement),
          mvt.type_caisse || '-',
          fmtPdf(montant),
          fmtPdf(cumul),
        ];
      });
      rows.push(['', '', '', '', '', '', 'TOTAL', fmtPdf(cumul), '']);
      autoTable(doc, {
        startY: y + 2,
        head: [['Heure', 'Type', 'Client', 'Detail / Bijou', 'Reference', 'Mode', 'Caisse', 'Montant', 'Cumul']],
        body: rows,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [88, 28, 135], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 18 },
          1: { cellWidth: 20 },
          2: { cellWidth: 30 },
          3: { cellWidth: 50 },
          4: { cellWidth: 28 },
          5: { cellWidth: 20 },
          6: { cellWidth: 22 },
          7: { cellWidth: 24, halign: 'right' },
          8: { cellWidth: 24, halign: 'right' },
        },
        margin: { left: 10, right: 10 },
        didParseCell: function (data) {
          if (data.row.index === rows.length - 1) {
            data.cell.styles.fillColor = [230, 230, 230];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
    }

    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(`Imprime le ${dateStr} a ${heureStr}`, pageW / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
    doc.save(`caisse_${boutiqueNom_local.replace(/\s/g, '_')}_${dateImpression}.pdf`);
  };

  const ouvrirCaisse = async () => {
    setSubmitting(true); setError('');
    try {
      await api.post(`${API_URL}/clotures-caisse/ouvrir`, { boutique_id: boutiqueId, fond_de_caisse: parseFloat(fondDeCaisse)||0 });
      setSuccess('Caisse ouverte'); setShowOuvrirModal(false); setFondDeCaisse(0);
      await loadAll(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const cloturerCaisse = async () => {
    if (!caisseActive?.id) return;
    setSubmitting(true); setError('');
    try {
      const totalEspeces = calculerEspeces();
      const totalCarte = parseFloat(carteComptee)||0;
      const totalCheque = parseFloat(chequeCompte)||0;
      const totalVir = parseFloat(virementCompte)||0;
      await api.post(`${API_URL}/clotures-caisse/${caisseActive.id}/cloturer`, {
        total_especes_compte: totalEspeces, total_carte_compte: totalCarte,
        total_cheque_compte: totalCheque, total_virement_compte: totalVir,
        commentaire
      });
      const totalCloture = totalEspeces + totalCarte + totalCheque + totalVir;
      setSuccess('Caisse cloturee'); setShowCloturerModal(false);
      notifySuccess(`Caisse cloturee — total ${totalCloture.toLocaleString('fr-FR')} DH`);
      setBillets200(0);setBillets100(0);setBillets50(0);setBillets20(0);
      setPieces10(0);setPieces5(0);setPieces2(0);setPieces1(0);setPieces05(0);
      setCarteComptee(0);setChequeCompte(0);setVirementCompte(0);setCommentaire('');
      await loadAll(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur cloture';
      setError(msg);
      notifyError(msg, { code: err.response?.data?.code, status: err.response?.status });
    }
    finally { setSubmitting(false); }
  };

  // En mode central, rediriger vers caisse generale
  const ctxType = (() => { try { return JSON.parse(localStorage.getItem('workspaceContext') || '{}').type; } catch { return ''; } })();
  if (ctxType === 'central') return <Navigate to="/caisse-generale" replace />;

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>;

  const enc = rapport?.encaissements || {};
  const resume = rapport?.resume || {};
  const ventesJour = rapport?.ventes || [];

  // Filtrage comptes clients
  const comptesFiltres = comptesClients.filter(c =>
    !filtreCompte || c.nom?.toLowerCase().includes(filtreCompte.toLowerCase()) ||
    c.telephone?.includes(filtreCompte)
  );

  const modes = [
    { key: 'especes', label: 'ESPECES', icon: Banknote, color: 'from-green-500 to-emerald-600', bg: 'bg-green-50', text: 'text-green-700' },
    { key: 'carte', label: 'CARTE BANCAIRE', icon: CreditCard, color: 'from-blue-500 to-blue-600', bg: 'bg-blue-50', text: 'text-blue-700' },
    { key: 'cheque', label: 'CHEQUE', icon: Building, color: 'from-indigo-500 to-indigo-600', bg: 'bg-indigo-50', text: 'text-indigo-700' },
    { key: 'virement', label: 'VIREMENT', icon: DollarSign, color: 'from-purple-500 to-purple-600', bg: 'bg-purple-50', text: 'text-purple-700' },
    { key: 'or', label: 'OR (DEDUCTION)', icon: Gem, color: 'from-yellow-500 to-amber-600', bg: 'bg-yellow-50', text: 'text-yellow-700' },
    { key: 'compte', label: 'COMPTE CLIENT', icon: Users, color: 'from-gray-500 to-gray-600', bg: 'bg-gray-50', text: 'text-gray-700' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">{getBoutiqueNom()}</h1>
              <p className="text-purple-200 text-sm">Caisse du {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            <div className="flex gap-2">
              {activeTab === 'classique' && (
                <>
                  {caisseActive ? (
                    <button onClick={() => setShowCloturerModal(true)}
                      className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 px-5 py-2.5 rounded-lg font-medium shadow">
                      <Lock className="h-4 w-4" /><span>Cloturer la caisse</span>
                    </button>
                  ) : (
                    <button onClick={() => setShowOuvrirModal(true)}
                      className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 px-5 py-2.5 rounded-lg font-medium shadow">
                      <Unlock className="h-4 w-4" /><span>Ouvrir la caisse</span>
                    </button>
                  )}
                  <button onClick={imprimerCaisse} className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2.5 rounded-lg">
                    <Printer className="h-4 w-4" /><span className="hidden sm:inline">Imprimer</span>
                  </button>
                </>
              )}
              <button onClick={loadAll} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="flex">
            <button onClick={() => setActiveTab('classique')}
              className={`flex-1 px-4 py-3 font-medium text-sm transition ${activeTab === 'classique' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              Vue classique
            </button>
            <button onClick={() => setActiveTab('rapport-jour')}
              className={`flex-1 px-4 py-3 font-medium text-sm transition ${activeTab === 'rapport-jour' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              Rapport du jour (3 blocs)
            </button>
          </div>
        </div>

        {activeTab === 'rapport-jour' ? (
          <RapportJour onImprimer={imprimerCaisse} />
        ) : (
        <>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between"><div className="flex items-center space-x-2"><AlertTriangle className="h-5 w-5" /><span>{error}</span></div><button onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center space-x-2"><CheckCircle className="h-5 w-5" /><span>{success}</span></div>}

        {/* Status caisse */}
        <div className={`rounded-xl p-4 border-2 flex items-center justify-between ${caisseActive ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
          <div className="flex items-center space-x-3">
            {caisseActive ? <Unlock className="h-7 w-7 text-green-600" /> : <Lock className="h-7 w-7 text-red-500" />}
            <div>
              <p className={`font-bold text-lg ${caisseActive ? 'text-green-800' : 'text-red-800'}`}>
                {caisseActive ? 'CAISSE OUVERTE' : 'CAISSE FERMEE'}
              </p>
              {caisseActive && <p className="text-sm text-green-600">Depuis {fmtHeure(caisseActive.date_ouverture)} — Fond: {fmt(caisseActive.fond_de_caisse)}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Ventes du jour</p>
            <p className="text-3xl font-black text-gray-900">{resume.nb_ventes || 0}</p>
          </div>
        </div>

        {/* ===================== ENCAISSEMENTS DU JOUR ===================== */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            <span>ENCAISSEMENTS DU JOUR</span>
          </h2>

          {/* Grosses cartes par mode */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {modes.map(m => {
              const montant = enc[m.key] || 0;
              const Icon = m.icon;
              return (
                <div key={m.key} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className={`bg-gradient-to-r ${m.color} px-4 py-2 flex items-center space-x-2`}>
                    <Icon className="h-4 w-4 text-white" />
                    <span className="text-white text-xs font-bold tracking-wide">{m.label}</span>
                  </div>
                  <div className="px-4 py-4">
                    <p className={`text-2xl sm:text-3xl font-black ${m.text}`}>{fmt(montant)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* GRAND TOTAL */}
          <div className="mt-4 bg-gradient-to-r from-purple-700 to-indigo-700 rounded-xl p-5 flex items-center justify-between shadow-lg">
            <div className="text-white">
              <p className="text-sm font-medium opacity-80">TOTAL ENCAISSE</p>
              <p className="text-xs opacity-60">{resume.nb_ventes || 0} vente{(resume.nb_ventes||0) > 1 ? 's' : ''} — Remises: {fmt(resume.total_remises)}</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black text-white">{fmt(enc.total)}</p>
          </div>
        </div>

        {/* ===================== DETAIL PAR BOUTIQUE (central) ===================== */}
        {isCentral && (rapport?.par_boutique || []).length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="px-5 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                <Store className="h-5 w-5 text-purple-600" /><span>DETAIL PAR BOUTIQUE</span>
              </h2>
            </div>
            <div className="divide-y">
              {(rapport.par_boutique).map(b => (
                <div key={b.boutique_id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="font-bold text-gray-900">{b.boutique_nom}</p>
                    <p className="text-xs text-gray-500">{b.nb_ventes} vente{b.nb_ventes > 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {parseFloat(b.especes) > 0 && <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 font-medium">Especes: {fmt(b.especes)}</span>}
                    {parseFloat(b.carte) > 0 && <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 font-medium">Carte: {fmt(b.carte)}</span>}
                    {parseFloat(b.cheque) > 0 && <span className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 font-medium">Cheque: {fmt(b.cheque)}</span>}
                    {parseFloat(b.virement) > 0 && <span className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-700 font-medium">Virement: {fmt(b.virement)}</span>}
                  </div>
                  <p className="text-xl font-black text-gray-900">{fmt(b.total)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===================== COMPTES CLIENTS ===================== */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setShowComptes(!showComptes)}>
            <h2 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
              <Users className="h-5 w-5 text-purple-600" />
              <span>COMPTES CLIENTS</span>
              {comptesClients.length > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                  {comptesClients.filter(c => parseFloat(c.solde_compte) < 0).length} debiteur{comptesClients.filter(c => parseFloat(c.solde_compte) < 0).length > 1 ? 's' : ''}
                </span>
              )}
            </h2>
            {showComptes ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </div>

          {showComptes && (
            <div>
              {/* Filtre */}
              <div className="px-5 py-3 border-b bg-gray-50">
                <input type="text" placeholder="Rechercher un client (nom ou telephone)..."
                  value={filtreCompte} onChange={e => setFiltreCompte(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>

              {/* Mobile */}
              <div className="md:hidden divide-y">
                {comptesFiltres.length === 0 ? (
                  <p className="px-4 py-8 text-center text-gray-500">Aucun client avec compte</p>
                ) : comptesFiltres.map(c => {
                  const solde = parseFloat(c.solde_compte || 0);
                  const or_g = parseFloat(c.solde_or_grammes || 0);
                  const isDebiteur = solde < 0;
                  return (
                    <div key={c.id} className="p-4 border-b last:border-b-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full mt-1 ${isDebiteur ? 'bg-red-500' : solde > 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <div>
                            <p className="font-bold text-gray-900">{c.nom}</p>
                            <p className="text-xs text-gray-500">{c.telephone || '-'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-black ${isDebiteur ? 'text-red-600' : 'text-green-600'}`}>
                            {isDebiteur ? '-' : ''}{fmt(Math.abs(solde))}
                          </p>
                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${isDebiteur ? 'bg-red-100 text-red-700' : solde > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isDebiteur ? 'DOIT' : solde > 0 ? 'AVOIR' : '-'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-6 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                        <div>{c.nb_achats || 0} achat{(c.nb_achats||0) > 1 ? 's' : ''} — total: {fmt(c.total_depense)}</div>
                        {or_g > 0 && <div className="text-yellow-600 font-medium">{or_g}g or</div>}
                        {c.dernier_bijou_nom && <div className="col-span-2">Dernier: <span className="font-medium text-gray-700">{c.dernier_bijou_nom}</span> ({fmt(c.dernier_prix)})</div>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tel</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Solde</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Or</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Achats</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total depense</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernier achat</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {comptesFiltres.length === 0 ? (
                      <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">Aucun client</td></tr>
                    ) : comptesFiltres.map(c => {
                      const solde = parseFloat(c.solde_compte || 0);
                      const or_g = parseFloat(c.solde_or_grammes || 0);
                      const isDebiteur = solde < 0;
                      const dernierDate = c.dernier_achat ? fmtDate(c.dernier_achat).split(' ')[0] : '-';
                      return (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                              isDebiteur ? 'bg-red-100 text-red-700' : solde > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {isDebiteur ? 'DOIT' : solde > 0 ? 'AVOIR' : '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-bold text-gray-900">{c.nom}</p>
                            {c.ville && <p className="text-xs text-gray-400">{c.ville}</p>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{c.telephone || '-'}</td>
                          <td className={`px-4 py-3 text-right font-black text-lg ${isDebiteur ? 'text-red-600' : solde > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {solde !== 0 ? `${isDebiteur ? '-' : ''}${fmt(Math.abs(solde))}` : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {or_g > 0 ? <span className="font-bold text-yellow-600">{or_g}g</span> : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-medium text-gray-700">{c.nb_achats || 0}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-700">{fmt(c.total_depense)}</td>
                          <td className="px-4 py-3">
                            {c.dernier_bijou_nom ? (
                              <div>
                                <p className="text-sm font-medium text-gray-900">{c.dernier_bijou_nom}</p>
                                <p className="text-xs text-gray-500">
                                  {c.dernier_bijou_ref ? `${c.dernier_bijou_ref} — ` : ''}{fmt(c.dernier_prix)} — {dernierDate}
                                </p>
                                <p className="text-xs text-gray-400">{c.dernier_mode_paiement}</p>
                              </div>
                            ) : <span className="text-gray-400 text-sm">-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ===================== VENTES DU JOUR ===================== */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setShowVentes(!showVentes)}>
            <h2 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
              <ShoppingBag className="h-5 w-5 text-purple-600" />
              <span>VENTES DU JOUR ({ventesJour.length})</span>
            </h2>
            {showVentes ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </div>
          {showVentes && (
            <>
              {/* Mobile */}
              <div className="md:hidden divide-y">
                {ventesJour.length === 0 ? <p className="px-4 py-8 text-center text-gray-500">Aucune vente</p>
                : ventesJour.map(v => (
                  <div key={v.id} className="p-4 cursor-pointer hover:bg-purple-50" onClick={() => loadDetailVente(v.id)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{v.bijou_nom || v.reference_article || `#${v.id}`}</p>
                        <p className="text-xs text-gray-500">{fmtHeure(v.date_vente)} {v.client_nom ? `— ${v.client_nom}` : ''}</p>
                      </div>
                      <p className="font-bold text-gray-900">{fmt(v.montant_final)}</p>
                    </div>
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{v.mode_paiement}</span>
                      {v.montant_or_deduit > 0 && <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">Or: {fmt(v.montant_or_deduit)}</span>}
                      {v.remise_montant > 0 && <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700">-{fmt(v.remise_montant)}</span>}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Heure</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Mode</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remise</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Or</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Encaisse</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {ventesJour.length === 0 ? <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">Aucune vente</td></tr>
                    : ventesJour.map(v => (
                      <tr key={v.id} className="hover:bg-purple-50 cursor-pointer" onClick={() => loadDetailVente(v.id)} title="Cliquer pour voir le detail des paiements">
                        <td className="px-4 py-3 text-sm text-gray-600">{fmtHeure(v.date_vente)}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{v.bijou_nom || '-'}<br/><span className="text-xs text-gray-400">{v.reference_article||''}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{v.client_nom || '-'}</td>
                        <td className="px-4 py-3 text-center"><span className="text-xs px-2 py-1 rounded bg-gray-100 font-medium">{v.mode_paiement}</span></td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(v.prix_vente)}</td>
                        <td className="px-4 py-3 text-sm text-right text-orange-600">{v.remise_montant > 0 ? `-${fmt(v.remise_montant)}` : '-'}</td>
                        <td className="px-4 py-3 text-sm text-right text-yellow-600">{v.montant_or_deduit > 0 ? fmt(v.montant_or_deduit) : '-'}</td>
                        <td className="px-4 py-3 text-right font-bold">{fmt(v.montant_final)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ===================== HISTORIQUE ===================== */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b">
            <h2 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
              <Clock className="h-5 w-5 text-purple-600" /><span>HISTORIQUE CLOTURES</span>
            </h2>
          </div>
          {/* Mobile */}
          <div className="md:hidden divide-y">
            {historique.length === 0 ? <p className="px-4 py-8 text-center text-gray-500">Aucune cloture</p>
            : historique.slice(0, 20).map(c => {
              const ecart = parseFloat(c.ecart_total||0);
              return (
                <div key={c.id} className="p-4 flex items-center justify-between" onClick={() => setShowDetailModal(c)}>
                  <div>
                    <p className="font-medium text-gray-900">{fmtDate(c.date_ouverture).split(' ')[0]}</p>
                    <p className="text-xs text-gray-500">{fmtHeure(c.date_ouverture)} — {c.date_cloture ? fmtHeure(c.date_cloture) : '...'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{fmt(c.total_theorique)}</p>
                    <p className={`text-xs font-bold ${ecartColor(ecart)}`}>Ecart: {fmt(ecart)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ouverture</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fermeture</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Theorique</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Compte</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ecart</th>
                  <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {historique.length === 0 ? <tr><td colSpan="7" className="px-5 py-8 text-center text-gray-500">Aucune cloture</td></tr>
                : historique.slice(0, 30).map(c => {
                  const ecart = parseFloat(c.ecart_total||0);
                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 text-sm">{fmtDate(c.date_ouverture).split(' ')[0]}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{fmtHeure(c.date_ouverture)}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{c.date_cloture ? fmtHeure(c.date_cloture) : '-'}</td>
                      <td className="px-5 py-3 text-sm text-right font-medium">{fmt(c.total_theorique)}</td>
                      <td className="px-5 py-3 text-sm text-right font-medium">{c.total_compte != null ? fmt(c.total_compte) : '-'}</td>
                      <td className="px-5 py-3 text-right"><span className={`inline-block px-2 py-0.5 rounded font-bold text-sm ${ecartColor(ecart)}`}>{fmt(ecart)}</span></td>
                      <td className="px-5 py-3 text-center"><button onClick={() => setShowDetailModal(c)} className="text-purple-600 hover:text-purple-800"><Eye className="h-5 w-5" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </div>

      {/* ===== MODAL OUVRIR ===== */}
      {showOuvrirModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center space-x-3"><Unlock className="h-6 w-6" /><h3 className="text-lg font-semibold">Ouvrir la caisse</h3></div>
              <button onClick={() => setShowOuvrirModal(false)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fond de caisse (MAD)</label>
              <input type="text" inputMode="decimal" onFocus={e => e.target.select()} value={fondDeCaisse}
                onChange={e => setFondDeCaisse(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-lg" placeholder="0.00" />
              <p className="text-xs text-gray-500 mt-1">Especes dans la caisse a l'ouverture</p>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setShowOuvrirModal(false)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={ouvrirCaisse} disabled={submitting} className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50">
                {submitting ? 'Ouverture...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL CLOTURER ===== */}
      {showCloturerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-4xl sm:my-8">
            <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center space-x-3"><Lock className="h-6 w-6" /><h3 className="text-lg font-semibold">Cloture de caisse</h3></div>
              <button onClick={() => setShowCloturerModal(false)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Theoriques */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <h4 className="font-bold text-purple-800 mb-3">Theorique (calcule automatiquement)</h4>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {modes.map(m => (
                    <div key={m.key} className="bg-white rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 font-medium">{m.label}</p>
                      <p className="text-sm font-bold">{fmt(enc[m.key])}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-2 bg-purple-100 rounded-lg p-2 text-center">
                  <p className="text-xs text-purple-600">TOTAL</p>
                  <p className="text-xl font-black text-purple-800">{fmt(enc.total)}</p>
                </div>
              </div>

              {/* Comptage especes */}
              <div className="border rounded-xl p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center space-x-2"><Banknote className="h-5 w-5 text-green-600" /><span>Comptage especes</span></h4>
                <div className="grid grid-cols-4 gap-2">
                  {[{l:'200',v:billets200,s:setBillets200,m:200},{l:'100',v:billets100,s:setBillets100,m:100},{l:'50',v:billets50,s:setBillets50,m:50},{l:'20',v:billets20,s:setBillets20,m:20}].map(i => (
                    <div key={i.l}>
                      <label className="text-xs text-gray-500">{i.l} MAD x</label>
                      <input type="text" inputMode="numeric" onFocus={e=>e.target.select()} value={i.v} onChange={e=>i.s(parseInt(e.target.value)||0)}
                        className="w-full px-2 py-1.5 border rounded-lg text-center text-sm" />
                      <p className="text-[10px] text-gray-400 text-center">= {(i.v*i.m).toFixed(0)}</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {[{l:'10',v:pieces10,s:setPieces10,m:10},{l:'5',v:pieces5,s:setPieces5,m:5},{l:'2',v:pieces2,s:setPieces2,m:2},{l:'1',v:pieces1,s:setPieces1,m:1},{l:'0.5',v:pieces05,s:setPieces05,m:0.5}].map(i => (
                    <div key={i.l}>
                      <label className="text-xs text-gray-500">{i.l} x</label>
                      <input type="text" inputMode="numeric" onFocus={e=>e.target.select()} value={i.v} onChange={e=>i.s(parseInt(e.target.value)||0)}
                        className="w-full px-2 py-1.5 border rounded-lg text-center text-sm" />
                    </div>
                  ))}
                </div>
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex justify-between items-center">
                  <span className="font-medium text-green-800">Total especes</span>
                  <span className="text-xl font-black text-green-800">{fmt(calculerEspeces())}</span>
                </div>
              </div>

              {/* Autres */}
              <div className="grid grid-cols-3 gap-3">
                {[{l:'Carte',v:carteComptee,s:setCarteComptee,t:enc.carte},{l:'Cheque',v:chequeCompte,s:setChequeCompte,t:enc.cheque},{l:'Virement',v:virementCompte,s:setVirementCompte,t:enc.virement}].map(i => (
                  <div key={i.l} className="border rounded-xl p-3">
                    <label className="text-xs font-medium text-gray-600">{i.l} (MAD)</label>
                    <input type="text" inputMode="decimal" onFocus={e=>e.target.select()} value={i.v} onChange={e=>i.s(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-center text-lg mt-1" />
                    <div className={`mt-1.5 text-xs px-2 py-1 rounded flex justify-between ${ecartColor((i.t||0) - parseFloat(i.v||0))}`}>
                      <span>Ecart</span><span className="font-bold">{fmt((i.t||0) - parseFloat(i.v||0))}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Ecart total */}
              {(() => {
                const tTheo = (enc.especes||0) + (enc.carte||0) + (enc.cheque||0) + (enc.virement||0);
                const tCompte = calculerTotalCompte();
                const ecart = tTheo - tCompte;
                return (
                  <div className={`rounded-xl p-4 border-2 flex items-center justify-between ${ecartColor(ecart)}`}>
                    <div>
                      <p className="font-bold text-lg">ECART TOTAL</p>
                      <p className="text-sm opacity-75">Theo: {fmt(tTheo)} | Compte: {fmt(tCompte)}</p>
                    </div>
                    <p className="text-3xl font-black">{fmt(ecart)}</p>
                  </div>
                );
              })()}

              <textarea value={commentaire} onChange={e => setCommentaire(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg" rows="2" placeholder="Commentaire (optionnel)..." />
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setShowCloturerModal(false)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={cloturerCaisse} disabled={submitting}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center space-x-2">
                <Lock className="h-4 w-4" /><span>{submitting ? 'En cours...' : 'Confirmer la cloture'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL DETAIL ===== */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl sm:my-8 max-h-[95vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg font-semibold">Detail cloture</h3>
              <button onClick={() => setShowDetailModal(null)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Ouverture</p><p className="font-medium">{fmtDate(showDetailModal.date_ouverture)}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Cloture</p><p className="font-medium">{fmtDate(showDetailModal.date_cloture)}</p></div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-600">Fond de caisse</p>
                <p className="text-lg font-bold text-blue-800">{fmt(showDetailModal.fond_de_caisse)}</p>
              </div>
              <table className="w-full text-sm border rounded-xl overflow-hidden">
                <thead className="bg-gray-50">
                  <tr><th className="px-4 py-2 text-left text-xs text-gray-500">Mode</th><th className="px-4 py-2 text-right text-xs text-gray-500">Theorique</th><th className="px-4 py-2 text-right text-xs text-gray-500">Compte</th><th className="px-4 py-2 text-right text-xs text-gray-500">Ecart</th></tr>
                </thead>
                <tbody className="divide-y">
                  {[{l:'Especes',t:showDetailModal.total_especes_theorique,c:showDetailModal.total_especes_compte},{l:'Carte',t:showDetailModal.total_carte_theorique,c:showDetailModal.total_carte_compte},{l:'Cheque',t:showDetailModal.total_cheque_theorique,c:showDetailModal.total_cheque_compte},{l:'Virement',t:showDetailModal.total_virement_theorique,c:showDetailModal.total_virement_compte}].map(r => {
                    const d = parseFloat(r.t||0) - parseFloat(r.c||0);
                    return <tr key={r.l}><td className="px-4 py-2 font-medium">{r.l}</td><td className="px-4 py-2 text-right">{fmt(r.t)}</td><td className="px-4 py-2 text-right">{fmt(r.c)}</td><td className="px-4 py-2 text-right"><span className={`px-2 py-0.5 rounded font-bold ${ecartColor(d)}`}>{fmt(d)}</span></td></tr>;
                  })}
                  <tr className="bg-purple-50 font-bold">
                    <td className="px-4 py-3 text-purple-800">TOTAL</td>
                    <td className="px-4 py-3 text-right text-purple-800">{fmt(showDetailModal.total_theorique)}</td>
                    <td className="px-4 py-3 text-right text-purple-800">{fmt(showDetailModal.total_compte)}</td>
                    <td className="px-4 py-3 text-right"><span className={`px-2 py-0.5 rounded font-bold ${ecartColor(showDetailModal.ecart_total)}`}>{fmt(showDetailModal.ecart_total)}</span></td>
                  </tr>
                </tbody>
              </table>
              {showDetailModal.commentaire && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"><p className="text-xs text-yellow-600 font-medium mb-1">Commentaire</p><p className="text-gray-700">{showDetailModal.commentaire}</p></div>}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end">
              <button onClick={() => setShowDetailModal(null)} className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL DETAIL VENTE (SUJET 2) ===================== */}
      {detailVente && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
             onClick={() => setDetailVente(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Detail vente #{detailVente.vente.id}</h3>
                <p className="text-xs text-purple-100">{fmtHeure(detailVente.vente.date_vente)}</p>
              </div>
              <button onClick={() => setDetailVente(null)} className="hover:bg-white/20 rounded p-1"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase">Article</p>
                <p className="font-semibold text-gray-900">{detailVente.vente.bijou_nom || '-'}</p>
                {detailVente.vente.reference_article && <p className="text-xs text-gray-500">{detailVente.vente.reference_article}</p>}
                <p className="text-xs text-gray-500 uppercase mt-2">Client</p>
                <p className="font-semibold text-gray-900">{detailVente.vente.client_nom || '-'}</p>
                {detailVente.vente.client_telephone && <p className="text-xs text-gray-500">{detailVente.vente.client_telephone}</p>}
              </div>

              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Paiements ({detailVente.paiements.length})</p>
                <div className="divide-y border rounded-lg">
                  {detailVente.paiements.length === 0
                    ? <p className="px-4 py-3 text-sm text-gray-500">Aucun paiement enregistre</p>
                    : detailVente.paiements.map((p, i) => (
                      <div key={i} className="px-4 py-2 flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 uppercase">{p.mode}{p.reference ? ` (${p.reference})` : ''}</span>
                        <span className={`text-sm font-bold ${p.montant < 0 ? 'text-gray-500' : 'text-gray-900'}`}>{fmt(p.montant)}</span>
                      </div>
                    ))}
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-700">Prix vente</span><span className="font-medium">{fmt(detailVente.totaux.prix_vente)}</span></div>
                {detailVente.totaux.remise_montant > 0 && <div className="flex justify-between text-sm text-orange-600"><span>Remise</span><span>-{fmt(detailVente.totaux.remise_montant)}</span></div>}
                <div className="flex justify-between text-sm font-semibold pt-2 border-t border-purple-200"><span>Montant final</span><span>{fmt(detailVente.totaux.montant_final)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-700">Encaisse (cash + or)</span><span className="font-bold text-green-700">{fmt(detailVente.totaux.total_encaisse_reel)}</span></div>
                {detailVente.totaux.mis_en_compte > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-gray-700">Mis en compte client</span><span className="font-bold text-amber-700">{fmt(detailVente.totaux.mis_en_compte)}</span></div>
                )}
              </div>

              {detailVente.vente.commentaire && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-gray-700">{detailVente.vente.commentaire}</div>}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end">
              <button onClick={() => setDetailVente(null)} className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClotureCaisse;
